
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Utility function to check if Stripe connection is still valid
 * This is useful after updating Stripe accounts or API keys
 */
export async function verifyStripeConnection(): Promise<boolean> {
  try {
    console.log('[Stripe Check] Verifying Stripe connection status');
    
    // First check if the user has a Stripe account connected
    const { data: profile } = await supabase
      .from('profiles')
      .select('stripe_account_id')
      .single();
    
    if (!profile?.stripe_account_id) {
      console.warn('[Stripe Check] No Stripe account ID found');
      return false;
    }
    
    // Test the connection by making a simple request
    const response = await supabase.functions.invoke('stripe-connection-check', {
      body: { accountId: profile.stripe_account_id }
    });
    
    if (response.error) {
      console.error('[Stripe Check] Connection test failed:', response.error);
      return false;
    }
    
    console.log('[Stripe Check] Connection verified successfully');
    return true;
  } catch (error) {
    console.error('[Stripe Check] Error verifying connection:', error);
    return false;
  }
}

/**
 * Hook for checking and handling Stripe connection status
 */
export function useStripeConnectionCheck() {
  const { toast } = useToast();
  
  const checkConnection = async () => {
    const isConnected = await verifyStripeConnection();
    
    if (isConnected) {
      toast({
        title: "Stripe connection verified",
        description: "Your Stripe account is properly connected and working.",
      });
      return true;
    } else {
      toast({
        title: "Stripe connection issue",
        description: "There might be an issue with your Stripe connection. Please check your settings.",
        variant: "destructive"
      });
      return false;
    }
  };
  
  return { checkConnection };
}
